<template>
  <div v-if="filteredGroups.length" class="related-agenda-items" data-testid="related-agenda-items">
    <v-subheader class="mt-4 pl-3">{{ $t('business.relatedAgendaItems') }}</v-subheader>
    <v-list dense class="related-history py-0">
      <template v-for="group in filteredGroups">
        <template v-for="meeting in group.meetings">
          <template v-for="agendaItem in meeting.agenda_items">
            <v-list-item
              :key="`agenda-item-${agendaItem.id}`"
              :to="{ name: 'agendaitem', params: { id: agendaItem.id } }"
              class="related-history__item"
              :title="meeting.title"
              data-testid="related-agenda-item"
            >
              <v-list-item-content class="py-2">
                <div class="related-history__context caption text--secondary">
                  <span class="font-weight-medium">{{ group.committee.title }}</span>
                  <span class="mx-1">·</span>
                  <time :datetime="meeting.start">{{ toLocaleDateString(meeting.start) }}</time>
                </div>
                <v-list-item-title class="text-wrap text-break">{{ agendaItem.title }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-icon class="my-0 ml-2 align-self-center">
                <v-icon>mdi-chevron-right</v-icon>
              </v-list-item-icon>
            </v-list-item>
            <div
              v-if="agendaItem.documents.length"
              :key="`agenda-item-documents-${agendaItem.id}`"
              class="related-history__documents px-4 pb-3"
            >
              <div
                v-for="document in agendaItem.documents"
                :key="`agenda-item-document-${agendaItem.id}-${document.type}-${document.id}`"
                class="related-history__document"
              >
                <v-chip
                  small
                  outlined
                  class="related-history__document-chip"
                  :to="documentLink(document)"
                  :title="document.title || documentTypeLabel(document.type)"
                  data-testid="related-agenda-item-document"
                >
                  <v-icon left small>mdi-file</v-icon>
                  <span class="text-truncate">{{ document.title || documentTypeLabel(document.type) }}</span>
                </v-chip>
                <v-btn
                  v-if="canCopyNotes(document)"
                  icon
                  x-small
                  class="ml-1"
                  :title="$t('business.copyNotes')"
                  :aria-label="$t('business.copyNotes')"
                  data-testid="copy-related-document-notes"
                  :disabled="copyingDocumentIds.includes(documentKey(document))"
                  :loading="copyingDocumentIds.includes(documentKey(document))"
                  @click.prevent.stop="copyNotes(document)"
                >
                  <v-icon small>mdi-import</v-icon>
                </v-btn>
              </div>
            </div>
          </template>
        </template>
      </template>
    </v-list>
  </div>
</template>

<script>
import { api } from '@/api'
import { createLink } from '@/api/helpers'
import { toLocaleDateString } from '@/filters'

export default {
  name: 'RelatedAgendaItems',
  props: {
    businessId: {
      type: Number,
      default: null,
    },
    currentAgendaItemId: {
      type: Number,
      default: null,
    },
    currentDocument: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      groups: [],
      copyingDocumentIds: [],
      requestId: 0,
    }
  },
  computed: {
    filteredGroups() {
      return this.groups
        .map((group) => ({
          ...group,
          meetings: group.meetings
            .map((meeting) => ({
              ...meeting,
              agenda_items: meeting.agenda_items.filter((agendaItem) => agendaItem.id !== this.currentAgendaItemId),
            }))
            .filter((meeting) => meeting.agenda_items.length)
            .sort((a, b) => new Date(b.start) - new Date(a.start)),
        }))
        .filter((group) => group.meetings.length)
    },
  },
  watch: {
    businessId: {
      immediate: true,
      handler() {
        this.fetchRelatedAgendaItems()
      },
    },
  },
  methods: {
    toLocaleDateString,
    documentKey(document) {
      return `${document.type}-${document.id}`
    },
    documentLink(document) {
      const query = document.note_layer_id
        ? {
            version_id: document.note_version_id,
            layer_id: document.note_layer_id,
          }
        : { version_id: document.version_id }
      return { name: document.type, params: { id: document.id }, query }
    },
    documentTypeLabel(documentType) {
      return documentType === 'proposal'
        ? this.$t('business.documentTypes.proposal')
        : this.$t('business.documentTypes.attachment')
    },
    canCopyNotes(document) {
      return !!(
        this.currentDocument &&
        this.currentDocument.externalId &&
        this.currentDocument.versionId &&
        document.note_layer_id &&
        document.note_version_id &&
        document.external_id === this.currentDocument.externalId
      )
    },
    async copyNotes(sourceDocument) {
      const key = this.documentKey(sourceDocument)
      this.copyingDocumentIds.push(key)
      try {
        const response = await api.post(
          createLink('businesses/{id}/copy_related_document_notes', { id: this.businessId }),
          {
            source_document_id: sourceDocument.id,
            source_document_type: sourceDocument.type,
            source_layer_id: sourceDocument.note_layer_id,
            source_version_id: sourceDocument.note_version_id,
            target_document_id: this.currentDocument.id,
            target_document_type: this.currentDocument.type,
            target_version_id: this.currentDocument.versionId,
          },
        )
        await this.currentDocument.refreshAnnotationLayers()
        const query = {
          ...this.$route.query,
          version_id: this.currentDocument.versionId,
          layer_id: response.data.layer_id,
        }
        const isCopiedLayerOpen =
          String(this.$route.query.version_id) === String(query.version_id) &&
          String(this.$route.query.layer_id) === String(query.layer_id)
        if (!isCopiedLayerOpen) {
          await this.$router.replace({ query })
        }
        this.notifySuccess(this.copyNotesMessage(response.data.copied))
      } catch (e) {
        this.notifyError(
          e.response && e.response.data.detail ? e.response.data.detail : this.$t('business.copyNotesError'),
        )
      } finally {
        this.copyingDocumentIds = this.copyingDocumentIds.filter((id) => id !== key)
      }
    },
    copyNotesMessage(copied) {
      if (copied) {
        return this.$t('business.copyNotesSuccess')
      }
      return this.$t('business.copyNotesAlreadyCopied')
    },
    async fetchRelatedAgendaItems() {
      const requestId = ++this.requestId
      if (!this.businessId) {
        this.groups = []
        return
      }

      try {
        const response = await api.get(createLink('businesses/{id}/related_agenda_items', { id: this.businessId }))
        if (requestId === this.requestId) {
          this.groups = response.data
        }
      } catch (e) {
        if (requestId === this.requestId) {
          this.groups = []
          this.notifyError(this.$t('business.error.fetchAgendaItem'))
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.related-history__item {
  min-height: 0;
}

.related-history__context {
  line-height: 1.4;
}

.related-history__documents {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.related-history__document {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
}

.related-history__document-chip {
  max-width: 100%;

  ::v-deep .v-chip__content {
    max-width: 100%;
  }
}
</style>
